import { motion } from 'framer-motion'
import { Link, Routes, Route, Navigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  Workflow,
  PlayCircle,
  Sparkles,
} from 'lucide-react'
import { suite, steps, readiness, funnelUrl, stepIndex } from '../content/aiOpsSuite'
import { stepIcons } from '../content/aiOpsIcons'
import UIScreens, { screensFor } from '../components/UIScreens'

function ReadinessChip({ status }) {
  const r = readiness[status]
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium shrink-0 ${r.className}`}>
      {r.label}
    </span>
  )
}

function StepRail({ activeId }) {
  return (
    <ol className="flex flex-wrap gap-2 mb-10">
      {steps.map((s, i) => {
        const active = s.id === activeId
        return (
          <li key={s.id}>
            <Link
              to={`/ai-operations/${s.id}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                active
                  ? 'bg-synth-600/30 border-synth-500/60 text-synth-200'
                  : 'bg-void-900/40 border-void-800/60 text-void-400 hover:text-void-200 hover:border-void-700'
              }`}
            >
              <span className="font-mono">{i + 1}</span>
              {s.verb}
            </Link>
          </li>
        )
      })}
    </ol>
  )
}

function SuiteIndex() {
  return (
    <div className="pt-24 pb-16">
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-synth-950/30 to-void-950" />
        <div className="absolute inset-0 grid-overlay opacity-30" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-synth-900/50 border border-synth-700/50 text-synth-300 text-sm font-medium mb-6">
              <Workflow className="w-4 h-4" />
              {suite.eyebrow}
            </span>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-6 max-w-3xl">
              {suite.headline}
            </h1>
            <p className="text-xl text-void-400 max-w-3xl leading-relaxed mb-8">{suite.summary}</p>
            <div className="flex flex-wrap gap-3">
              <Link to={`/ai-operations/${steps[0].id}`} className="btn-primary inline-flex items-center gap-2">
                Walk through it in {steps.length} steps <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/demo" className="btn-secondary inline-flex items-center gap-2">
                <PlayCircle className="w-4 h-4" /> Take the guided demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-4">
          {suite.pillars.map((p) => (
            <div key={p.title} className="p-5 rounded-xl bg-void-900/30 border border-void-800/50">
              <h2 className="font-display font-semibold text-void-100 mb-1">{p.title}</h2>
              <p className="text-void-400 text-sm leading-relaxed">{p.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-display font-bold text-void-50 mb-2">From question to running workflow</h2>
          <p className="text-void-400 mb-8 max-w-2xl">
            {steps.length} spaces, in the order you'd actually use them. Each one says plainly what's live today
            and what's still being built.
          </p>
          <ol className="space-y-4">
            {steps.map((s, i) => {
              const Icon = stepIcons[s.id]
              return (
                <motion.li
                  key={s.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={`/ai-operations/${s.id}`}
                    className="group flex items-start gap-5 p-6 rounded-2xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all"
                  >
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <span className="font-mono text-xs text-void-500">{String(i + 1).padStart(2, '0')}</span>
                      <div className="p-3 rounded-xl bg-gradient-to-br from-synth-500 to-signal-600">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <h3 className="text-lg font-display font-semibold text-void-100 group-hover:text-synth-300 transition-colors">
                          {s.verb}: {s.name}
                        </h3>
                        <ReadinessChip status={s.status} />
                      </div>
                      <p className="text-void-400 text-sm leading-relaxed">{s.tagline}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-void-600 group-hover:text-synth-400 shrink-0 self-center transition-colors" />
                  </Link>
                </motion.li>
              )
            })}
          </ol>
          <p className="text-void-500 text-sm mt-6 max-w-2xl">
            Readiness levels are founder-assessed per component, not independent attestations.
          </p>
        </div>
      </section>

      <SubscribeCTA content="hub" />
    </div>
  )
}

function SubscribeCTA({ content }) {
  return (
    <section className="py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 md:p-10 rounded-3xl bg-gradient-to-br from-synth-900/50 to-void-900 border border-synth-800/30">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-void-50 mb-3">
            The suite comes with your 23Controls™ subscription
          </h2>
          <p className="text-void-400 mb-6 max-w-2xl">
            Create your account and Identity Anchor on 23Controls™, choose a plan, and the AI Operations
            Suite is part of your workspace. Want help adopting it? We offer training and hands-on
            onboarding.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href={funnelUrl('/signup', content)} className="btn-primary inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Start your subscription
            </a>
            <Link to="/services" className="btn-secondary inline-flex items-center gap-2">
              Training & onboarding <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

function StepPage() {
  const { stepId } = useParams()
  const idx = stepIndex(stepId)
  if (idx === -1) return <Navigate to="/ai-operations" replace />

  const step = steps[idx]
  const prev = steps[idx - 1]
  const next = steps[idx + 1]
  const Icon = stepIcons[step.id]
  const screens = screensFor(step.id)

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/ai-operations"
          className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          AI Operations Suite
        </Link>

        <StepRail activeId={step.id} />

        <motion.div key={step.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs font-semibold text-synth-400 uppercase tracking-wider mb-3">
            Step {idx + 1} of {steps.length} · {step.verb}
          </p>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-synth-500 to-signal-600">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50">{step.name}</h1>
            <ReadinessChip status={step.status} />
          </div>
          <p className="text-xl text-void-300 mb-6 max-w-3xl">{step.tagline}</p>
          <p className="text-void-400 leading-relaxed mb-12 max-w-3xl">{step.description}</p>

          <h2 className="text-xl font-display font-bold text-void-50 mb-4">How it works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {step.flow.map((f, i) => (
              <div key={f.label} className="p-5 rounded-xl bg-void-900/30 border border-void-800/50">
                <span className="font-mono text-xs text-synth-400">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-display font-semibold text-void-100 mt-1 mb-1">{f.label}</h3>
                <p className="text-void-400 text-sm leading-relaxed">{f.detail}</p>
              </div>
            ))}
          </div>

          <h2 className="text-xl font-display font-bold text-void-50 mb-4">What you'll see</h2>
          <ul className="space-y-2 mb-8">
            {step.views.map((v) => (
              <li key={v} className="flex items-start gap-2 text-void-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-synth-500 shrink-0 mt-0.5" />
                {v}
              </li>
            ))}
          </ul>

          {screens.length > 0 && (
            <div className="mb-12">
              <UIScreens key={step.id} screens={screens} />
            </div>
          )}

          <div className="p-5 rounded-xl border border-void-800/50 bg-void-900/20 mb-12 max-w-3xl">
            <p className="text-xs font-semibold text-void-500 uppercase tracking-wider mb-1">Where it stands</p>
            <p className="text-void-400 text-sm leading-relaxed">{step.honesty}</p>
          </div>

          <div className="flex flex-wrap items-center gap-6 mb-12">
            {step.secondaryCta && (
              <Link to={step.secondaryCta.href} className="btn-secondary inline-flex items-center gap-2 text-sm">
                {step.secondaryCta.label} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link
              to={`/demo?scene=${step.id}`}
              className="inline-flex items-center gap-2 text-synth-400 hover:text-synth-300 font-medium text-sm"
            >
              <PlayCircle className="w-4 h-4" /> See this step in the guided demo
            </Link>
          </div>

          <nav className="flex items-center justify-between gap-4 pt-8 border-t border-void-800/50">
            {prev ? (
              <Link to={`/ai-operations/${prev.id}`} className="inline-flex items-center gap-2 text-void-400 hover:text-void-200">
                <ArrowLeft className="w-4 h-4" /> {prev.verb}: {prev.name}
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/ai-operations/${next.id}`} className="btn-primary inline-flex items-center gap-2">
                Next — {next.verb}: {next.name} <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <a href={funnelUrl('/signup', step.id)} className="btn-primary inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Start your subscription
              </a>
            )}
          </nav>
        </motion.div>
      </div>

      {!next && <SubscribeCTA content={`${step.id}-footer`} />}
    </div>
  )
}

export default function AIOperationsPage() {
  return (
    <Routes>
      <Route index element={<SuiteIndex />} />
      <Route path=":stepId" element={<StepPage />} />
    </Routes>
  )
}
